import enumStrings from "../../../enumStrings";
import { STAFF_SUPPORT_EMAIL } from "../../../utils/userFacingError";

export const getAuthErrorName = (error: unknown): string => {
  if (!error || typeof error !== "object") return "";
  if ("name" in error && typeof error.name === "string") return error.name;
  if ("code" in error && typeof error.code === "string") return error.code;
  return "";
};

const getAuthErrorText = (error: unknown): string =>
  error && typeof error === "object" && "message" in error &&
  typeof error.message === "string"
    ? error.message
    : "";

const TOO_MANY_SIGN_IN_ATTEMPTS_TEXT =
  "You tried to sign in too many times. Wait a few minutes, then try again, or select Forgot your password?";
const USER_DISABLED_TEXT =
  "This account cannot access the staff dashboard. Contact your chamber administrator for help.";

/**
 * Cognito sends NotAuthorizedException for several causes. Its message text
 * tells them apart, so each cause gets its own next step.
 */
const getNotAuthorizedMessage = (error: unknown): string => {
  const text = getAuthErrorText(error).toLowerCase();
  if (text.includes("password attempts exceeded")) {
    return TOO_MANY_SIGN_IN_ATTEMPTS_TEXT;
  }
  if (text.includes("user is disabled")) {
    return USER_DISABLED_TEXT;
  }
  if (text.includes("temporary password has expired")) {
    return "Your temporary password has expired. Ask your chamber administrator to send you a new one.";
  }
  return enumStrings.pages.common.errors.invalidUsernameOrPassword;
};

export const getSignInErrorMessage = (error: unknown): string => {
  switch (getAuthErrorName(error)) {
    case "MemberDashboardRequired":
      return "This is a member account. Sign in with your password on the Member Dashboard.";
    case "UserDisabled":
      return USER_DISABLED_TEXT;
    case "NetworkError":
      return enumStrings.pages.common.errors.networkError;
    case "NotAuthorizedException":
      return getNotAuthorizedMessage(error);
    case "UserNotFoundException":
      return enumStrings.pages.common.errors.invalidUsernameOrPassword;
    case "PasswordResetRequiredException":
      return "You need to reset your password before you can sign in. Select Forgot your password? to get a code.";
    case "UserNotConfirmedException":
      return "This account is not set up yet. Contact your chamber administrator for help.";
    case "UserDoesNotHaveAccessToChamberMemberPro":
      return enumStrings.pages.common.errors.userDoesNotHaveAccessToChamberMemberPro;
    case "IncompleteDashboardSession":
      return `We could not finish signing you in. Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
    case "ApolloError":
      // The account check after the password step did not finish.
      return `We could not finish signing you in. Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
    case "LimitExceededException":
    case "TooManyRequestsException":
      return enumStrings.pages.common.errors.tooManyAttempts;
    default:
      return "We could not sign you in. Check your email and password and try again. If it keeps happening, select Forgot your password? or contact your chamber administrator.";
  }
};

export const temporaryPasswordGuidance =
  "You cannot reset the password for this account here. If you got a temporary password by email, sign in with the latest one, then set a new password. If you need help, contact your chamber administrator.";
